import { useState, useCallback } from "react";

export const useMultiStepForm = () => {
  // Step management
  const [currentStep, setCurrentStep] = useState(3);

  // Step 1: Verify Email
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [otp, setOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [userType, setUserType] = useState("INDIVIDUAL_USER"); // or "COMPANY"


  // Step 2: Verify BVN
  const [bvn, setBvn] = useState("");
  const [dob, setDob] = useState("");
  const [bvnVerifying, setBvnVerifying] = useState(false);
  const [bvnUserDetails, setBvnUserDetails] = useState(null);
  const [bvnError, setBvnError] = useState("");

  // Step 3: BVN Last 4 Digits
  const [bvnLast4, setBvnLast4] = useState("");
  const [bvnVerified, setBvnVerified] = useState(false);

  // Step 4: Password & Referrals
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordShown1, setPasswordShown1] = useState(false);
  const [passwordShown2, setPasswordShown2] = useState(false);
  const [referralOption, setReferralOption] = useState("");
  const [sourceNotInList, setSourceNotInList] = useState("");
  const [isUserNewsLetters, setIsUserNewsLetters] = useState(false);
  const [isUserTerms, setIsUserTerms] = useState(false);

  // Validation errors
  const [stepErrors, setStepErrors] = useState({});

  // Track verified steps (lock back navigation for verified steps)
  const [emailVerifiedAtStep, setEmailVerifiedAtStep] = useState(false);
  const [bvnVerifiedAtStep, setBvnVerifiedAtStep] = useState(false);

  // Validation functions
  const validateEmail = () => {
    const errors = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Invalid email format";
    }

    if (!otp) {
      errors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(otp)) {
      errors.otp = "OTP must be 6 digits";
    }

    return errors;
  };

  const validateCompanyName = () => {
    if (userType === "COMPANY") {
      if (!companyName || companyName.trim().length === 0) {
        return "Company name is required";
      }
      if (companyName.trim().length < 2) {
        return "Company name must be at least 2 characters";
      }
      if (companyName.length > 100) {
        return "Company name must be under 100 characters";
      }
    }
    return "";
  };

  const validateBVN = () => {
    const errors = {};

    if (!bvn) {
      errors.bvn = "BVN is required";
    } else if (!/^\d{11}$/.test(bvn)) {
      errors.bvn = "BVN must be 11 digits";
    }

    if (!dob) {
      errors.dob = "Date of birth is required";
    }

    return errors;
  };

  const validateBVNLast4 = () => {
    const errors = {};

    if (!bvnLast4) {
      errors.bvnLast4 = "Last 4 digits are required";
    } else if (!/^\d{4}$/.test(bvnLast4)) {
      errors.bvnLast4 = "Must be exactly 4 digits";
    }

    // Verify that the last 4 digits match the BVN
    const bvnLast4Actual = bvnUserDetails?.mobile?.slice(-4);
    if (bvnLast4 !== bvnLast4Actual) {
      errors.bvnLast4 = "Last 4 digits do not match BVN";
    }

    return errors;
  };

  const validatePassword = () => {
    const errors = {};

    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    // if (!isUserTerms) {
    //   errors.terms = "You must accept the terms and conditions";
    // }

    return errors;
  };

  // Navigation
  const goToNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      setStepErrors({});
    }
  };

  const goToPreviousStep = () => {
    // Prevent going back from Step 2 if email is verified
    if (currentStep === 2 && emailVerifiedAtStep) {
      return { blocked: true, reason: "Email already verified. Cannot edit." };
    }
    // Prevent going back from Step 3+ if BVN is verified
    if (currentStep >= 3 && bvnVerifiedAtStep) {
      return { blocked: true, reason: "BVN already verified. Cannot edit." };
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setStepErrors({});
      return { blocked: false };
    }
    return { blocked: false };
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= 4) {
      setCurrentStep(step);
      setStepErrors({});
    }
  };

  // Reset all form state
  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setEmail("");
    setCompanyName("");
    setOtp("");
    setEmailVerified(false);
    setBvn("");
    setDob("");
    setBvnVerifying(false);
    setBvnUserDetails(null);
    setBvnError("");
    setBvnLast4("");
    setBvnVerified(false);
    setPassword("");
    setConfirmPassword("");
    setPasswordShown1(false);
    setPasswordShown2(false);
    setReferralOption("");
    setSourceNotInList("");
    setIsUserNewsLetters(false);
    setIsUserTerms(false);
    setStepErrors({});
    setEmailVerifiedAtStep(false);
    setBvnVerifiedAtStep(false);
  }, []);

  // Compile complete form data
  const getCompleteFormData = () => {
    return {
      email,
      otp,
      emailVerified,
      bvn,
      dob,
      bvnLast4,
      bvnVerified,
      bvnUserDetails,
      password,
      confirmPassword,
      referralOption,
      isUserNewsLetters,
      isUserTerms,
    };
  };

  return {
    // Current step
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    resetForm,

    // Step 1: Email verification
    email,
    setEmail,
    companyName,
    setCompanyName,
    otp,
    setOtp,
    emailVerified,
    setEmailVerified,
    userType,
    setUserType,
    // Step 2: BVN verification
    bvn,
    setBvn,
    dob,
    setDob,
    bvnVerifying,
    setBvnVerifying,
    bvnUserDetails,
    setBvnUserDetails,
    bvnError,
    setBvnError,

    // Step 3: BVN confirmation
    bvnLast4,
    setBvnLast4,
    bvnVerified,
    setBvnVerified,

    // Step 4: Password & Referral
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordShown1,
    setPasswordShown1,
    passwordShown2,
    setPasswordShown2,
    referralOption,
    setReferralOption,
    sourceNotInList,
    setSourceNotInList,
    isUserNewsLetters,
    setIsUserNewsLetters,
    isUserTerms,
    setIsUserTerms,

    // Errors
    stepErrors,
    setStepErrors,

    // Verified steps tracking
    emailVerifiedAtStep,
    setEmailVerifiedAtStep,
    bvnVerifiedAtStep,
    setBvnVerifiedAtStep,

    // Validation
    validateEmail,
    validateCompanyName,
    validateBVN,
    validateBVNLast4,
    validatePassword,

    // Form data
    getCompleteFormData,
  };
};
